
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface BandPercentages {
  A: number;
  B: number;
  C: number;
  D: number;
}

interface BandingSimulationPanelProps {
  bandPercentages: BandPercentages;
  onPercentageChange: (band: keyof BandPercentages, value: number[]) => void;
  onPercentageCommit: (band: keyof BandPercentages, value: number[]) => void;
  onReset?: () => void;
  currentUser?: any;
}

const BandingSimulationPanel = ({ 
  bandPercentages, 
  onPercentageChange, 
  onPercentageCommit,
  onReset,
  currentUser
}: BandingSimulationPanelProps) => {

  const saveIndividualBandValue = async (band: keyof BandPercentages, value: number) => {
    if (!currentUser) {
      console.error("No current user for saving band value");
      return;
    }

    try {
      console.log(`Saving Band ${band} value: ${value} for user: ${currentUser.id}`);
      
      const updateData: any = {
        user_id: currentUser.id,
      };
      
      // Set the specific band field
      if (band === 'A') updateData.band_a = value;
      else if (band === 'B') updateData.band_b = value;
      else if (band === 'C') updateData.band_c = value;
      else if (band === 'D') updateData.band_d = value;

      const { error } = await supabase
        .from('banding_simulation')
        .upsert(updateData, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error(`Error saving Band ${band} value:`, error);
        toast.error(`Error saving Band ${band} value`);
      } else {
        console.log(`Successfully saved Band ${band} value: ${value}`);
        toast.success(`Band ${band} value saved: ${value}%`);
      }
    } catch (error) {
      console.error(`Error saving Band ${band} value:`, error);
      toast.error(`Error saving Band ${band} value`);
    }
  };

  const handleBandCommit = (band: keyof BandPercentages, value: number[]) => {
    const newValue = value[0];
    console.log(`Band ${band} drag completed with value: ${newValue}`);
    
    // Call the original handler
    onPercentageCommit(band, value);
    
    // Save directly to database
    saveIndividualBandValue(band, newValue);
  };

  return (
    <Card className="bg-secondary/50 border-primary/20 h-full w-[450px]">
      <CardContent className="p-3 h-full flex flex-col w-[450px] border rounded-xl border-primary">
        <div className="flex justify-between items-center mb-3 px-4">
          <h3 className="text-white text-[20px] font-semibold">Banding Simulation</h3>
          {onReset && (
            <Button 
              onClick={onReset} 
              className="bg-primary text-black hover:bg-primary/80" 
              size="sm"
            >
              Reset Banding
            </Button>
          )}
        </div>
        <div className="flex-1 flex flex-col justify-between py-[10px]">
          <div className="flex items-center gap-1">
            <span className="text-blue-400 text-xs w-25 px-4">Band A:</span>
            <span className="text-primary/70 text-xs w-8">0%</span>
            <div className="flex-1 relative">
              <div className="text-white text-[10px] text-center absolute -top-6 left-1/2 transform -translate-x-1/2">
                {bandPercentages.A}%
              </div>
              <Slider 
                value={[bandPercentages.A]} 
                onValueChange={value => onPercentageChange('A', value)}
                onValueCommit={value => handleBandCommit('A', value)}
                min={0} 
                max={15} 
                step={1} 
                className="w-full h-1" 
              />
            </div>
            <span className="text-primary/70 text-xs w-8">15%</span>
          </div>
          
          <div className="flex items-center gap-1">
            <span className="text-green-400 text-xs w-25 px-4">Band B:</span>
            <span className="text-primary/70 text-xs w-8">15%</span>
            <div className="flex-1 relative">
              <div className="text-white text-[10px] text-center absolute -top-6 left-1/2 transform -translate-x-1/2">
                {bandPercentages.B}%
              </div>
              <Slider 
                value={[bandPercentages.B]} 
                onValueChange={value => onPercentageChange('B', value)}
                onValueCommit={value => handleBandCommit('B', value)}
                min={15} 
                max={35} 
                step={1} 
                className="w-full h-1" 
              />
            </div>
            <span className="text-primary/70 text-xs w-8">35%</span>
          </div>
          
          <div className="flex items-center gap-1">
            <span className="text-amber-400 text-xs w-25 px-4">Band C:</span>
            <span className="text-primary/70 text-xs w-8">45%</span>
            <div className="flex-1 relative">
              <div className="text-white text-[10px] text-center absolute -top-6 left-1/2 transform -translate-x-1/2">
                {bandPercentages.C}%
              </div>
              <Slider 
                value={[bandPercentages.C]} 
                onValueChange={value => onPercentageChange('C', value)}
                onValueCommit={value => handleBandCommit('C', value)}
                min={45} 
                max={65} 
                step={1} 
                className="w-full h-1" 
              />
            </div>
            <span className="text-primary/70 text-xs w-8">65%</span>
          </div>
          
          <div className="flex items-center gap-1">
            <span className="text-red-400 text-xs w-25 px-4">Band D:</span>
            <span className="text-primary/70 text-xs w-8">0%</span>
            <div className="flex-1 relative">
              <div className="text-white text-[10px] text-center absolute -top-6 left-1/2 transform -translate-x-1/2">
                {bandPercentages.D}%
              </div>
              <Slider 
                value={[bandPercentages.D]} 
                onValueChange={value => onPercentageChange('D', value)}
                onValueCommit={value => handleBandCommit('D', value)}
                min={0} 
                max={15} 
                step={1} 
                className="w-full h-1" 
              />
            </div>
            <span className="text-primary/70 text-xs w-8">15%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BandingSimulationPanel;
